import {fetchAuthedApiResponse} from './http';

type SearchErrorCode = 'rate_limit'|'timeout'|'access'|'unavailable';
export class CrmSearchError extends Error {
  readonly retryAt:number;
  readonly code:SearchErrorCode;
  readonly retryAfterSeconds:number;
  readonly status?:number;
  constructor(code:SearchErrorCode, retryAfterSeconds=0, status?:number) {
    super(code==='timeout'?'Search timed out':code==='access'?'Search access unavailable':code==='rate_limit'?'Too many searches':'Search source unavailable');
    this.name='CrmSearchError';
    this.code=code;this.retryAfterSeconds=retryAfterSeconds;this.status=status;
    this.retryAt=Date.now()+retryAfterSeconds*1000;
  }
}

/** Search-only deadline includes CSRF hydration, response headers and JSON body.
 * Race cancellation as well: a shared bootstrap need not honor our AbortSignal.
 * No query, server response body or exception text is copied into an error.
 */
export async function readCrmSearch<T>(path:string, init:RequestInit={}, signal?:AbortSignal):Promise<T> {
  const controller=new AbortController();
  const cancel=()=>controller.abort(signal?.reason);
  let timedOut=false;
  const timer=setTimeout(()=>{timedOut=true;controller.abort();},8000);
  signal?.addEventListener('abort',cancel,{once:true});
  if(signal?.aborted)cancel();
  let onAbort:()=>void=()=>{};
  const aborted=new Promise<never>((_resolve,reject)=>{
    onAbort=()=>reject(timedOut?new CrmSearchError('timeout'):new DOMException('Search cancelled','AbortError'));
    controller.signal.addEventListener('abort',onAbort,{once:true});
    if(controller.signal.aborted)onAbort();
  });
  const request=async()=>{
    if(controller.signal.aborted)throw new DOMException('Search cancelled','AbortError');
    const response=await fetchAuthedApiResponse(path,{...init,signal:controller.signal,cache:'no-store'});
    if(!response.ok){
      if(response.status===429){
        const raw=response.headers.get('Retry-After');
        const seconds=raw && /^\d+$/.test(raw)?Number(raw):raw?Math.ceil((Date.parse(raw)-Date.now())/1000):60;
        throw new CrmSearchError('rate_limit',Number.isFinite(seconds)?Math.min(3600,Math.max(1,seconds)):60,429);
      }
      throw new CrmSearchError(response.status===401||response.status===403?'access':'unavailable',0,response.status);
    }
    return await response.json() as T;
  };
  try {return await Promise.race([request(),aborted]);}
  catch(error){
    if(controller.signal.aborted){if(timedOut)throw new CrmSearchError('timeout');throw new DOMException('Search cancelled','AbortError');}
    if(typeof error==='object' && error!==null && 'name' in error && error.name==='AbortError')throw error;
    if(error instanceof CrmSearchError)throw error;
    throw new CrmSearchError('unavailable');
  } finally {
    clearTimeout(timer);signal?.removeEventListener('abort',cancel);
    controller.signal.removeEventListener('abort',onAbort);
  }
}
